/**
 * Groups `@communicator/shared`'s `CONFIG_FIELDS` (already transcribed from
 * docs/design-handoff/data/settings-model.json — task-18-brief.md says
 * "render from this, do not re-transcribe the JSON") into the sections the
 * settings page renders. Every FIELD-level fact (id, kind, label, hint,
 * tier, gate, unit, options, default) comes straight from CONFIG_FIELDS —
 * nothing about a field is re-typed here.
 *
 * Section-level chrome (title + one-line blurb) has no equivalent in
 * CONFIG_FIELDS at all (it only carries a `tier` and `gate` per field, both
 * uniform within a section). That chrome is page structure, not a
 * deployment- or brand-specific value — it doesn't touch a name, host,
 * path, endpoint or SLA number — so it is safe to state directly here,
 * unlike everything the page actually displays as data.
 *
 * TWO SECTIONS ARE NOT CONFIG. `users` (moved 2026-08-10 from its own
 * `/users` route into Settings — Andrew: it is staff configuration, so it
 * belongs with the rest of configuration) renders a live directory of
 * people with per-row actions, not a list of deployment values — there is
 * no `ConfigField` shape that honestly describes "a staff member's role",
 * and inventing fake field entries just to push it through the generic
 * `FieldRow` renderer would violate the very rule the paragraph above
 * states. `lines` (Task 6, spec 2026-09-05 §Settings) is the same shape for
 * a different reason: it is a live CRUD table over `/ops/lines`
 * (services/ops/src/routes/lines.ts), not a flat deployment value either.
 * `SettingsSection` is a discriminated union instead: a `'fields'` section
 * still carries only real `CONFIG_FIELDS` facts, and a `'custom'` section
 * carries none at all — it declares its own body component
 * (`SettingsPage.tsx` renders `UsersSection`/`LinesSection` for those) and
 * has no `fields` or `tier` to be honest or dishonest about in the first
 * place. `tier` in particular is deliberately absent from the custom
 * variant: it describes how a deployment VALUE takes effect (runtime-hot /
 * build-time / infra-time), and neither a staff roster nor a line roster is
 * one — giving either a tier anyway would be exactly the kind of fabricated
 * fact this file exists to refuse.
 */
import type { ConfigField, ConfigGate, ConfigTier } from '@communicator/shared'
import { CONFIG_FIELDS } from '@communicator/shared'

export interface FieldsSettingsSection {
  key: string
  label: string
  blurb: string
  body: 'fields'
  tier: ConfigTier
  gate: ConfigGate
  fields: ConfigField[]
}

/** See this file's header comment. No `fields`, no `tier` — there is nothing real to put there. */
export interface CustomSettingsSection {
  key: string
  label: string
  blurb: string
  body: 'custom'
  gate: ConfigGate
}

export type SettingsSection = FieldsSettingsSection | CustomSettingsSection

const SECTION_COPY: Record<string, { label: string; blurb: string }> = {
  personal: {
    label: 'Personal preferences',
    blurb: 'Yours only. None of this changes what other people see or how the queue sorts.',
  },
  brand: {
    label: 'Naming and brand',
    blurb:
      'Product name, client-facing copy and colours. Editing these renames the product without a redeploy. ' +
      "The client binary carries its own build-time copy of the name and target host.",
  },
  channels: {
    label: 'Channels and SLA',
    blurb:
      'One adapter per channel, each declaring how identity resolves, whether it is live, how a reply is ' +
      'sent, what waiting too long means, and what closing does.',
  },
  halo: {
    label: 'Halo integration',
    blurb:
      'Halo is long-term record, not a live mirror. Every write is gated on a thread being linked to a ' +
      'ticket, and each trigger pushes only the delta since the last-synced pointer.',
  },
  identity: {
    label: 'Identity and trust',
    blurb: 'Five tiers, two independent axes: identity confidence and client mapping.',
  },
  auth: {
    label: 'Enrolment and auth',
    blurb:
      'One enrolment with a sign-in state on top. Auth method keys off client type; session persistence ' +
      'keys off device trust.',
  },
  entra: {
    label: 'Staff sign-in with Entra',
    blurb:
      "Team members sign in with Entra against the operating MSP's own tenant, plain SSO. Sign-ins are " +
      'accepted from that one tenant and no other. Leave blank to use email plus a one-time code, which ' +
      'always keeps working alongside it.',
  },
  coverage: {
    label: 'Roles and coverage',
    blurb:
      'Availability is a property of the dispatch role. Flip into dispatch and you join the coverage pool.',
  },
  ai: {
    label: 'AI assistance',
    blurb:
      'Drafts guidance for the technician in the context pane — a next question, a tone read, a suggested ' +
      'action. Read-only by design: it never messages the customer, changes a priority or writes to Halo. ' +
      'The conversation transcript is sent to Anthropic to produce it; switch this off and nothing leaves.',
  },
  retention: {
    label: 'Transcripts and retention',
    blurb:
      'The verbatim transcript lives in the thread store, not in Halo, so the store is in scope for HIPAA ' +
      'retention and access. Where it lives and how long it is kept is a deliberate decision.',
  },
}

/**
 * The `'custom'`-body sections (see this file's header comment). Their own
 * small copy table, separate from `SECTION_COPY` above, because each carries
 * a `gate` (real page-structure chrome, same as every other section's) and
 * no `tier` at all — folding it into `SECTION_COPY` would need the shared
 * `{ label, blurb }` shape to grow a field only these entries ever use.
 */
const CUSTOM_SECTION_COPY: Record<string, { label: string; blurb: string; gate: ConfigGate }> = {
  users: {
    label: 'Users',
    blurb: 'Every staff member on this team, with the controls to change role, admin and active state where you may.',
    gate: 'admin',
  },
  lines: {
    label: 'Lines',
    blurb:
      'The phone numbers Tossker answers on. Conversations stick to the number they arrive on; new outbound ' +
      'conversations always start on the primary.',
    gate: 'admin',
  },
  aliases: {
    label: 'Aliases remembered',
    blurb:
      'Numbers a person pinned to a Halo contact via the Match modal, beside what Halo alone resolves that ' +
      'number to right now. A pin can go stale as a contact moves clients or gets reassigned — the rows ' +
      'where the two disagree are flagged so you can remove a bad one.',
    gate: 'admin',
  },
  // Not admin: the installer is served publicly under /updates anyway, and any
  // tech should be able to grab the link to send a client. `personal` just
  // marks it as not an admin-only tenant setting (custom sections render for
  // everyone regardless; this gate is chrome, see this file's header).
  apps: {
    label: 'Install the apps',
    blurb: 'Download the desktop client. These links are public — send them straight to anyone who needs the app.',
    gate: 'personal',
  },
}

/** Order matches docs/design-handoff/data/settings-model.json's `sections` array, with `users`, `lines` and `aliases` appended — none has an entry there (that file only ever described CONFIG_FIELDS-backed sections). */
export const SECTION_ORDER: readonly string[] = [
  'personal',
  'brand',
  'channels',
  'halo',
  'identity',
  'auth',
  'entra',
  'coverage',
  'retention',
  'ai',
  'users',
  'lines',
  'aliases',
  'apps',
]

/** Purely derived from the field-level `ConfigTier` enum — no separate
 * string table to drift out of sync with packages/shared/src/config.ts. */
export function tierLabel(tier: ConfigTier): string {
  return tier.toUpperCase()
}

export function buildSections(): SettingsSection[] {
  return SECTION_ORDER.flatMap((key): SettingsSection[] => {
    const custom = CUSTOM_SECTION_COPY[key]
    if (custom) {
      return [{ key, label: custom.label, blurb: custom.blurb, gate: custom.gate, body: 'custom' }]
    }
    const fields = CONFIG_FIELDS.filter((f) => f.section === key)
    if (fields.length === 0) return []
    const copy = SECTION_COPY[key]
    const tier = fields[0]?.tier ?? 'runtime-hot'
    const gate = fields[0]?.gate ?? 'admin'
    return [{ key, label: copy?.label ?? key, blurb: copy?.blurb ?? '', tier, gate, fields, body: 'fields' }]
  })
}

/**
 * The three fields the design leaves genuinely undecided (task-18-brief.md
 * / task-18 instructions, "Four things that must be exactly right" #1):
 * transcript retention (`r.window`), who may read a closed transcript
 * (`r.access`), and whether the highest-risk actions demand crypto
 * (`i.risk`). `r.window` and `i.risk` already default to the literal string
 * "Undecided" in CONFIG_FIELDS, but `r.access` does not — its options are
 * `["Dispatch and admin", "Admin only", "Anyone on the team"]` with no
 * "Undecided" choice at all, so its configured default ("Dispatch and
 * admin") would misrepresent a decision nobody has made. All three render
 * as an inert "Undecided" row on this page rather than a live control, so
 * none of them can show a plausible-looking answer that isn't real.
 */
export const OPEN_DECISION_FIELD_IDS: readonly string[] = ['r.window', 'r.access', 'i.risk']

/**
 * Fields nothing reads yet.
 *
 * The Settings page used to stamp a blanket "NOT WIRED" chip on EVERY section,
 * a leftover from when that was true of all of them. It stopped being true
 * section by section as things got built — Halo, timezone, business hours, the
 * SMS sending number — and the chip kept claiming otherwise, which is worse
 * than saying nothing: it teaches people that changing a setting here does
 * nothing, so they stop trying, and then a real setting goes unused.
 *
 * This is the inverse list, and it is deliberately maintained by hand.
 * "Is this value read anywhere" is not mechanically derivable — a field can be
 * read by a service this app never imports — so a wrong answer generated
 * confidently would be worse than one someone had to think about.
 *
 * VERIFIED by searching every service and app for each id. Currently EMPTY:
 * the last three — notification sound, desktop notifications and the default
 * context-pane state — were wired in the same pass that would otherwise have
 * shipped a page politely explaining that a third of its controls did nothing.
 * Labelling them was not a fix.
 */
export const UNWIRED_FIELD_IDS: readonly string[] = []

/**
 * How much of a section actually does something.
 *
 * `none` earns the "NOT WIRED" chip. `some` says so honestly rather than
 * rounding up to wired or down to not — a section where two of five fields do
 * nothing is a real state and pretending otherwise is how the original chip
 * became a lie.
 */
export function sectionWiring(
  fieldIds: readonly string[],
  unwiredIds: readonly string[] = UNWIRED_FIELD_IDS,
): 'all' | 'some' | 'none' {
  const unwired = fieldIds.filter((id) => unwiredIds.includes(id)).length
  if (unwired === 0) return 'all'
  if (unwired === fieldIds.length) return 'none'
  return 'some'
}

/**
 * Task 6 (settings/sign-in/history/phone repaint), README §7: "Settings' ten
 * flat sections regroup into four clusters" under 10px/600 `.1em` eyebrows in
 * the nav. This is presentation grouping ONLY — the task's own binding
 * decision 1 is explicit that "the section SET does not change", so
 * `SECTION_ORDER`/`buildSections()`/`settingsTab` above are untouched and a
 * deep link to any existing section key still works exactly as it did.
 *
 * The README's own cluster table names four items under CONNECTED SYSTEMS
 * ("Halo · Voice · 3CX · Sign-in and identity (merges Enrolment/auth, Entra,
 * Identity and trust)") that assume sections this repo does not have — there
 * is no standalone `voice` or `3cx` section (those fields live inside
 * `channels`), and binding decision 1 forbids actually merging `auth`/
 * `entra`/`identity` into one section just to match that literal wording. So
 * this maps the REAL eleven section keys onto the four cluster labels by the
 * same intent the table describes, without inventing or merging a section:
 * halo/identity/auth/entra (the real "systems the console talks to") sit
 * together under CONNECTED SYSTEMS, and channels/users/coverage/brand sit
 * together under HOW WE WORK, matching the README's own item order for that
 * cluster ("Channels and SLA · Users (admin) · Roles and coverage · Name and
 * brand").
 */
export type ClusterKey = 'you' | 'how-we-work' | 'connected-systems' | 'data'

export const CLUSTER_ORDER: readonly ClusterKey[] = ['you', 'how-we-work', 'connected-systems', 'data']

export const CLUSTER_LABEL: Record<ClusterKey, string> = {
  you: 'YOU',
  'how-we-work': 'HOW WE WORK',
  'connected-systems': 'CONNECTED SYSTEMS',
  data: 'DATA',
}

/** Every key in SECTION_ORDER must appear here exactly once — asserted in test/settings.test.tsx. */
const SECTION_CLUSTER: Record<string, ClusterKey> = {
  personal: 'you',
  channels: 'how-we-work',
  users: 'how-we-work',
  lines: 'how-we-work',
  coverage: 'how-we-work',
  brand: 'how-we-work',
  halo: 'connected-systems',
  identity: 'connected-systems',
  auth: 'connected-systems',
  entra: 'connected-systems',
  // Halo-identity administration — the pane is entirely about what Halo
  // resolves a number to, next to the alias table's own pinned answer.
  aliases: 'connected-systems',
  apps: 'how-we-work',
  retention: 'data',
  ai: 'data',
}

export function clusterOf(sectionKey: string): ClusterKey | undefined {
  return SECTION_CLUSTER[sectionKey]
}

export interface SettingsCluster {
  key: ClusterKey
  label: string
  sections: SettingsSection[]
}

export function buildClusters(): SettingsCluster[] {
  const sections = buildSections()
  return CLUSTER_ORDER.map((key) => ({
    key,
    label: CLUSTER_LABEL[key],
    sections: sections.filter((s) => SECTION_CLUSTER[s.key] === key),
  })).filter((c) => c.sections.length > 0)
}

/**
 * The gold count badge a cluster carries when it holds an unresolved
 * decision (README §7: "clusters with unresolved decisions carry a gold
 * count badge" — the retention section's own literal example is "2 open").
 * Derived from `OPEN_DECISION_FIELD_IDS` via each field's real
 * `CONFIG_FIELDS` section, never hand-counted, so it cannot drift out of
 * sync with that list the way a second, hardcoded count could.
 */
export function clusterOpenDecisionCount(clusterKey: ClusterKey): number {
  return OPEN_DECISION_FIELD_IDS.filter((id) => {
    const field = CONFIG_FIELDS.find((f) => f.id === id)
    return field !== undefined && SECTION_CLUSTER[field.section] === clusterKey
  }).length
}
